// react-native-linear-gradient has no web build. The previous noop shim rendered
// null AND dropped its children — every <LinearGradient>{children}</LinearGradient>
// silently disappeared (priority/escalation tags, gradient headers/buttons, etc.).
//
// This shim mirrors the react-native-linear-gradient API and renders children
// inside an RNW <View> painted with a real CSS linear-gradient() background.
// RNW 0.21's View accepts `backgroundImage` (View/types.js.flow lists it; the
// style compiler passes arbitrary props through), so the gradient paints and the
// children render — matching mobile.
const React = require("react");
const { View, StyleSheet } = require("react-native"); // → react-native-web

const DEFAULT_START = { x: 0.5, y: 0 };
const DEFAULT_END = { x: 0.5, y: 1 };

// Map an RN start→end vector to a CSS gradient angle.
// RN y axis points DOWN; CSS 0deg points UP, 90deg points RIGHT. The vector
// (dx, dy) in RN space becomes the CSS angle atan2(dx, -dy).
function vectorToAngle(start, end) {
  const s = start || DEFAULT_START;
  const e = end || DEFAULT_END;
  const dx = (e.x ?? 0.5) - (s.x ?? 0.5);
  const dy = (e.y ?? 1) - (s.y ?? 0);
  if (dx === 0 && dy === 0) return 180; // top→bottom default
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
}

function buildGradient({ colors, start, end, locations, useAngle, angle }) {
  const stops = (Array.isArray(colors) ? colors : [])
    .map((c, i) => {
      const loc =
        Array.isArray(locations) && typeof locations[i] === "number"
          ? ` ${Math.round(locations[i] * 100)}%`
          : "";
      return `${c}${loc}`;
    })
    .join(", ");
  // react-native-linear-gradient's `useAngle` switches to its own `angle` prop
  // (degrees, clockwise from 12 o'clock = same convention as CSS minus 0-point).
  // RNLG angle 0 = bottom→top, 90 = left→right. CSS 0deg = bottom→top too, so
  // RNLG angle maps directly to CSS deg.
  const deg = useAngle && typeof angle === "number" ? angle : vectorToAngle(start, end);
  return `linear-gradient(${deg}deg, ${stops})`;
}

function LinearGradient(props) {
  const {
    colors,
    start,
    end,
    locations,
    useAngle,
    angle,
    style,
    children,
    ...rest
  } = props || {};

  const grad = buildGradient({ colors, start, end, locations, useAngle, angle });
  // Flatten so an array style ([style, {...}]) is collapsed; then layer the
  // gradient on top. backgroundImage is honored by RNW 0.21's View.
  const flattened = StyleSheet.flatten(style) || {};

  return React.createElement(
    View,
    { ...rest, style: [flattened, { backgroundImage: grad }] },
    children,
  );
}

// Support default import, named { LinearGradient }, and CommonJS interop.
module.exports = LinearGradient;
module.exports.__esModule = true;
module.exports.default = LinearGradient;
module.exports.LinearGradient = LinearGradient;
